import { useEffect } from "react"

import { useQuery, useQueryClient } from "@tanstack/react-query"

import type { ChatSessionModel } from "@/api/models"

import { queryKeys } from "@/lib/query-keys"
import { ChatService } from "@/services/chat-service"

import {
  applyDurableSessionPlaceholders,
  subscribeDurableSessionPlaceholders,
} from "./durable-session-placeholders"
import { useChatSessionSyncListener } from "./use-chat-session-cross-tab-sync"
import { scheduleSessionListRefetch } from "../utils/session-list-refetch"

export function useSessionsQuery(sessionType: string, projectId?: string) {
  const chatService = ChatService.getInstance()
  const queryClient = useQueryClient()

  // Cross-window sync (issue #2548). Mounted HERE — beside the query whose
  // key it invalidates — so every window that renders a session list is
  // subscribed by construction, with the exact `sessionType`/`projectId`
  // scope of the list it is refreshing. A strict no-op while the
  // `cross_window_session_sync` flag is off.
  useChatSessionSyncListener({ sessionType, projectId })

  useEffect(() => {
    return subscribeDurableSessionPlaceholders((source) => {
      if (source !== "remote") return
      // Coalesced across every instance of this hook for the same list (a tab
      // mounts 5–7) — issue #3323 item C2.
      scheduleSessionListRefetch(queryClient, sessionType, projectId)
    })
  }, [projectId, queryClient, sessionType])

  return useQuery({
    queryKey: queryKeys.sessionHistory(sessionType, projectId),
    // `signal` aborts a request that a newer refetch superseded, instead of
    // leaving the old multi-megabyte response running (issue #3323 item C2).
    queryFn: async ({ signal }): Promise<ChatSessionModel[]> => {
      // Legacy behavior: The original "open" Explorer batches Navigator and DocumentInsight sessions
      // This maintains backward compatibility for users with existing sessions
      // BUT: Custom variants are isolated and only load their own sessions
      if (!projectId && sessionType === "open") {
        const sessions = await chatService.listSessionsBatch(
          ["open", "navigator", "document-insight"],
          signal
        )
        // Re-apply any in-flight durable chat's placeholder title (see
        // `durable-session-placeholders.ts`): a durable session stays untitled
        // server-side until the turn completes, so without this every refetch
        // during the turn would drop the brand-new chat out of the sidebar.
        // A pure pass-through when nothing is registered — i.e. always, on the
        // legacy transport and whenever the durable flag is off.
        return applyDurableSessionPlaceholders(sessions || [])
      }

      // All other routes (variants, translator, etc.) get their own isolated sessions
      const sessions = await chatService.listSessions(sessionType, projectId, signal)
      return applyDurableSessionPlaceholders(sessions || [])
    },
    refetchOnWindowFocus: true, // Critical data - refetch when user returns to tab
    staleTime: 1000 * 30, // Consider data fresh for 30 seconds
    gcTime: 1000 * 60 * 5, // Keep in cache for 5 minutes
  })
}
